import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Typography, Tag, notification, Popconfirm, Tooltip, Statistic, Row, Col, Alert } from 'antd';
import { PlusOutlined, SaveOutlined, CheckOutlined, UndoOutlined, DeleteOutlined, ThunderboltOutlined, ArrowLeftOutlined, BarChartOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { label } from '../../lib/format';
const MONTHS = Array.from({ length: 12 }, (_, i) => `m${String(i + 1).padStart(2, '0')}`);
const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const STATUS_COLORS = { DRAFT: 'default', APPROVED: 'green', CLOSED: 'blue' };
const monthLabels = (fyStart) => MONTHS.map((_, i) => moment(fyStart).add(i, 'months').format('MMM YY'));

/** The budget grid: P&L accounts (or any account added) x 12 months, natural sign (income and expenses positive). */
function BudgetEditor({ budgetId, onBack }) {
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId } = useBooks();
  const [budget, setBudget] = React.useState(null);
  const [accounts, setAccounts] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [dirty, setDirty] = React.useState(false);
  const [adding, setAdding] = React.useState(null);
  const [fillOpen, setFillOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [fillForm] = Form.useForm();

  const load = React.useCallback(() => {
    request.get(`/budgets/${budgetId}`).then((r) => r.json()).then((r) => {
      if (!r.status) return notification.error({ message: r.message });
      setBudget(r.data);
      setRows(r.data.BudgetLines.map((l) => ({ key: `${l.account_id}|${l.department_id || ''}`, account_id: l.account_id, department_id: l.department_id, account: l.Account, department: l.Department, values: Object.fromEntries(MONTHS.map((m) => [m, Number(l[m])])) })));
      setDirty(false);
    });
  }, [budgetId]);
  React.useEffect(load, [load]);
  React.useEffect(() => {
    if (!ledgerId) return;
    request.get('/accounts', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => r.status && setAccounts(r.data.filter((a) => !a.is_heading)));
  }, [ledgerId]);

  const editable = budget && budget.status === 'DRAFT';
  const labels = budget ? monthLabels(budget.fy_start) : MONTHS;
  const setCell = (key, m, v) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, values: { ...r.values, [m]: v } } : r)));
    setDirty(true);
  };
  const setAnnual = (key, annual) => {
    const per = Math.floor((Number(annual || 0) / 12) * 100) / 100;
    const values = Object.fromEntries(MONTHS.map((m, i) => [m, i === 11 ? Math.round((Number(annual || 0) - per * 11) * 100) / 100 : per]));
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, values } : r)));
    setDirty(true);
  };
  const addRow = () => {
    if (!adding) return;
    const account = accounts.find((a) => a.id === adding);
    const key = `${adding}|`;
    if (rows.some((r) => r.key === key)) return notification.info({ message: 'That account is already on the budget.' });
    setRows((rs) => [...rs, { key, account_id: adding, department_id: null, account, values: Object.fromEntries(MONTHS.map((m) => [m, 0])) }]);
    setAdding(null);
    setDirty(true);
  };
  const addAllPl = () => {
    const existing = new Set(rows.map((r) => r.key));
    const extra = accounts.filter((a) => a.shows_on_pl && !existing.has(`${a.id}|`)).map((a) => ({ key: `${a.id}|`, account_id: a.id, department_id: null, account: a, values: Object.fromEntries(MONTHS.map((m) => [m, 0])) }));
    if (!extra.length) return;
    setRows((rs) => [...rs, ...extra]);
    setDirty(true);
  };
  const removeRow = (key) => {
    setRows((rs) => rs.filter((r) => r.key !== key));
    setDirty(true);
  };
  const save = () => {
    setBusy(true);
    request
      .put(`/budgets/${budgetId}/lines`, { lines: rows.map((r) => ({ account_id: r.account_id, department_id: r.department_id, ...r.values })) })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: r.message });
        load();
      })
      .finally(() => setBusy(false));
  };
  const act = (action) =>
    request.post(`/budgets/${budgetId}/${action}`, {}).then((r) => r.json()).then((r) => {
      if (!r.status) return notification.error({ message: r.message });
      notification.success({ message: `Budget ${r.data.status.toLowerCase()}` });
      load();
    });
  const fill = () =>
    fillForm.validateFields().then((values) => {
      setBusy(true);
      request
        .post(`/budgets/${budgetId}/fill-from-actuals`, { from: values.from, to: values.to, uplift_pct: values.uplift_pct || 0 })
        .then((r) => r.json())
        .then((r) => {
          if (!r.status) return notification.error({ message: r.message });
          notification.success({ message: r.message });
          setFillOpen(false);
          load();
        })
        .finally(() => setBusy(false));
    });

  const annual = (r) => Math.round(MONTHS.reduce((s, m) => s + Number(r.values[m] || 0), 0) * 100) / 100;
  const sorted = [...rows].sort((a, b) => (a.account?.sort_order || 0) - (b.account?.sort_order || 0));
  const income = sorted.filter((r) => r.account && r.account.increase_side === 'Cr');
  const expenses = sorted.filter((r) => !r.account || r.account.increase_side !== 'Cr');
  const total = (list) => Math.round(list.reduce((s, r) => s + annual(r), 0) * 100) / 100;

  const columns = [
    {
      title: 'Account',
      key: 'a',
      fixed: 'left',
      width: 260,
      render: (_, r) => (
        <span>
          <Typography.Text type="secondary" style={{ display: 'inline-block', width: 48 }}>{r.account?.code}</Typography.Text>
          {r.account?.name}
          {r.department && <Tag style={{ marginLeft: 6 }}>{r.department.name}</Tag>}
        </span>
      ),
    },
    { title: 'Annual', key: 'annual', width: 120, align: 'right', render: (_, r) => (editable ? <InputNumber size="small" controls={false} style={{ width: 105 }} value={annual(r)} onChange={(v) => setAnnual(r.key, v)} /> : <strong>{money(annual(r))}</strong>) },
    ...MONTHS.map((m, i) => ({
      title: labels[i],
      key: m,
      width: 96,
      align: 'right',
      render: (_, r) => (editable ? <InputNumber size="small" controls={false} style={{ width: 84 }} value={r.values[m]} onChange={(v) => setCell(r.key, m, v)} /> : money(r.values[m])),
    })),
    ...(editable ? [{ title: '', key: 'x', width: 40, fixed: 'right', render: (_, r) => <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeRow(r.key)} /> }] : []),
  ];
  const section = (title, list) => (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong>{title}</Typography.Text>
      <Table
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={list}
        columns={columns}
        scroll={{ x: 1700 }}
        locale={{ emptyText: 'No lines' }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><strong>Total {title.toLowerCase()}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right"><strong>{money(total(list))}</strong></Table.Summary.Cell>
            {MONTHS.map((m, i) => (
              <Table.Summary.Cell key={m} index={i + 2} align="right"><strong>{money(list.reduce((s, r) => s + Number(r.values[m] || 0), 0))}</strong></Table.Summary.Cell>
            ))}
            {editable && <Table.Summary.Cell index={14} />}
          </Table.Summary.Row>
        )}
      />
    </div>
  );

  if (!budget) return null;
  const addOptions = accounts.filter((a) => !rows.some((r) => r.key === `${a.id}|`)).map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));
  const prevFrom = moment(budget.fy_start).subtract(1, 'year').format('YYYY-MM-DD');
  const prevTo = moment(budget.fy_end).subtract(1, 'year').format('YYYY-MM-DD');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16 }}>
        <div>
          <Button icon={<ArrowLeftOutlined />} type="link" onClick={onBack} style={{ paddingLeft: 0 }}>All budgets</Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {budget.name} <Tag color={STATUS_COLORS[budget.status]}>{budget.status}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">Financial year {budget.fy_start} to {budget.fy_end}. Amounts in natural sign: income and expenses both positive.</Typography.Text>
        </div>
        <Space wrap>
          <Link to={`/ledger-reports?tab=budget&budget=${budget.id}`}><Button icon={<BarChartOutlined />}>Budget vs Actual</Button></Link>
          {editable && <Button icon={<ThunderboltOutlined />} onClick={() => { fillForm.setFieldsValue({ from: prevFrom, to: prevTo, uplift_pct: 0 }); setFillOpen(true); }}>Fill from last year</Button>}
          {editable && <Button type="primary" icon={<SaveOutlined />} onClick={save} disabled={!dirty} loading={busy}>Save</Button>}
          {editable && isManager && (
            <Popconfirm title="Approve this budget? It becomes read-only." onConfirm={() => act('approve')} disabled={dirty}>
              <Button icon={<CheckOutlined />} disabled={dirty || !rows.length}>Approve</Button>
            </Popconfirm>
          )}
          {!editable && isManager && <Button icon={<UndoOutlined />} onClick={() => act('reopen')}>Reopen as draft</Button>}
        </Space>
      </div>
      {dirty && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Unsaved changes - press Save to keep them." />}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col><Statistic title="Budgeted income" value={money(total(income))} /></Col>
        <Col><Statistic title="Budgeted expenses" value={money(total(expenses))} /></Col>
        <Col><Statistic title="Budgeted result" value={money(total(income) - total(expenses))} valueStyle={{ color: total(income) - total(expenses) >= 0 ? '#389e0d' : '#cf1322' }} /></Col>
      </Row>
      {editable && (
        <Space wrap style={{ marginBottom: 12 }}>
          <Select showSearch optionFilterProp="label" placeholder="Add account" style={{ width: 340 }} value={adding} onChange={setAdding} options={addOptions} />
          <Button icon={<PlusOutlined />} onClick={addRow} disabled={!adding}>Add</Button>
          <Tooltip title="Adds every profit and loss account not yet on the budget, at zero">
            <Button onClick={addAllPl}>Add all P&L accounts</Button>
          </Tooltip>
        </Space>
      )}
      {section('Income', income)}
      {section('Expenses', expenses)}

      <Modal title="Fill from actuals" open={fillOpen} onCancel={() => setFillOpen(false)} onOk={fill} okText="Fill" confirmLoading={busy}>
        <Typography.Paragraph type="secondary">Copies the posted actuals of the reference period month by month into this budget (P&L accounts), uplifted by the percentage. Lines for the same accounts are replaced; other lines are kept.</Typography.Paragraph>
        <Form form={fillForm} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="from" label="From" rules={[{ required: true }]}><Input type="date" /></Form.Item>
            <Form.Item name="to" label="To" rules={[{ required: true }]}><Input type="date" /></Form.Item>
            <Form.Item name="uplift_pct" label="Uplift %"><InputNumber min={-100} max={1000} step={0.5} style={{ width: 120 }} /></Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

/** Budgeting (xlsx MENU "Accounting -> Budgeting"): one budget per financial year of the selected books. */
export default function BudgetsPage() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId, ledger } = useBooks();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [form] = Form.useForm();
  const budgetId = params.get('budget') ? Number(params.get('budget')) : null;

  const fetchRows = React.useCallback(() => {
    if (!ledgerId) return setRows([]);
    setLoading(true);
    request.get('/budgets', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => r.status && setRows(r.data)).finally(() => setLoading(false));
  }, [ledgerId]);
  React.useEffect(fetchRows, [fetchRows]);

  const openBudget = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('budget', String(id));
    else next.delete('budget');
    setParams(next);
  };
  const create = () =>
    form.validateFields().then((values) => {
      request.post('/budgets', { ...values, ledger_id: ledgerId }).then((r) => r.json()).then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        setOpen(false);
        fetchRows();
        openBudget(r.data.id);
      });
    });
  const remove = (b) => request.delete(`/budgets/${b.id}`).then((r) => r.json()).then((r) => (r.status ? fetchRows() : notification.error({ message: r.message })));

  if (budgetId) return <BudgetEditor budgetId={budgetId} onBack={() => { openBudget(null); fetchRows(); }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Budgeting
          </Typography.Title>
          <div><BooksCaption /></div>
          <Typography.Text type="secondary">One budget per financial year of the books: twelve monthly amounts per account, compared with the posted actuals in the Budget vs Actual report.</Typography.Text>
        </div>
        <Space wrap>
          <BooksSelector />
          <Button type="primary" icon={<PlusOutlined />} disabled={!ledgerId} onClick={() => { form.resetFields(); form.setFieldsValue({ year: new Date().getFullYear() }); setOpen(true); }}>
            New budget
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: ledgerId ? 'No budgets yet' : 'Choose the books first' }}
        columns={[
          { title: 'Budget', dataIndex: 'name', render: (v, b) => <Typography.Link onClick={() => openBudget(b.id)}>{v}</Typography.Link> },
          { title: 'Financial year', key: 'fy', render: (_, b) => `${b.fy_start} to ${b.fy_end}` },
          { title: 'Lines', dataIndex: 'lines', width: 80, align: 'right' },
          { title: 'Income', dataIndex: 'total_income', align: 'right', render: money },
          { title: 'Expenses', dataIndex: 'total_expenses', align: 'right', render: money },
          { title: 'Result', key: 'r', align: 'right', render: (_, b) => <strong>{money(b.total_income - b.total_expenses)}</strong> },
          { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
          {
            title: '',
            key: 'a',
            width: 200,
            render: (_, b) => (
              <Space size={4}>
                <Button size="small" onClick={() => openBudget(b.id)}>Open</Button>
                <Link to={`/ledger-reports?tab=budget&budget=${b.id}`}><Button size="small" icon={<BarChartOutlined />}>vs Actual</Button></Link>
                {isManager && b.status === 'DRAFT' && (
                  <Popconfirm title="Delete this draft budget?" onConfirm={() => remove(b)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      <Modal title="New budget" open={open} onCancel={() => setOpen(false)} onOk={create} okText="Create">
        <Typography.Paragraph type="secondary">The budget covers the financial year of the books that starts in the chosen year{ledger ? ` (year start month ${ledger.financial_year_start_month})` : ''}.</Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="year" label="Financial year starting in" rules={[{ required: true }]}><InputNumber min={2000} max={2100} style={{ width: 160 }} /></Form.Item>
          <Form.Item name="name" label="Name" tooltip="Defaults to 'Budget <year>'"><Input maxLength={120} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
